/**
 * Lobby-only all-time leaderboard panels. Two stateless factories:
 *
 *   - createLobbyLeaderboardTop(): left column — fixed top 10, with gold /
 *     silver / bronze accents for ranks 1-3.
 *   - createLobbyLeaderboardTail(): right column — positions 11+, ping-pong
 *     CSS-animation scrolling (~40s one direction, alternate). Zero JS after
 *     setRows() — the browser keeps the keyframes running even if the tab
 *     backgrounds, which keeps behavior predictable on OBS.
 *
 * Both panels use theme CSS custom properties so the color-picker applies,
 * and defer row sizing to {@link fitText} so long display names shrink to
 * fit rather than truncate.
 *
 * setRows() is idempotent: it edits existing rows in place so repeated calls
 * (e.g. from back-to-back state_snapshot frames on reconnect) don't rebuild
 * the DOM and don't restart the CSS ping-pong animation. That invisible
 * detail fixes a flash that the lobby exhibited when snapshots arrived in
 * rapid succession.
 */

import type { LbRow } from '@sg/shared';
import { fitText } from './fit-text.js';

export interface LobbyLeaderboardHandle {
  el: HTMLDivElement;
  setRows: (rows: LbRow[]) => void;
  dispose: () => void;
}

// --- Shared styles -----------------------------------------------------------
// Injected once per document. Both panels share visual language; rank-medal
// coloring (gold / silver / bronze) lives on per-row classes so the tail panel
// (ranks 11+) naturally gets the default palette.

const STYLES = `
  .sg-lblb {
    position: relative;
    height: 100%;
    display: flex;
    flex-direction: column;
    background: linear-gradient(180deg, rgba(6,12,233,0.55) 0%, rgba(3,4,40,0.75) 100%);
    border: 2px solid var(--sg-primary, #FFC43D);
    border-radius: 14px;
    box-shadow: 0 4px 30px rgba(0,0,0,0.4);
    color: #fff;
    font-family: 'Inter', system-ui, sans-serif;
    overflow: hidden;
    pointer-events: auto;
  }
  /* Left-column top-10 panel sizes to its content (header + 10 rows + pad)
     so the column can space-evenly distribute the three vertical gaps.
     Only the lobby TOP panel uses auto height — the right-column STANDINGS
     panel still wants height 100% for its ping-pong overflow math. */
  .sg-lblb[data-role="lobby-lb-top"] {
    height: auto;
  }
  .sg-lblb[data-role="lobby-lb-top"] .sg-lblb-body {
    flex: 0 0 auto;
    overflow: visible;
  }
  .sg-lblb[data-role="lobby-lb-top"] .sg-lblb-list {
    padding: 4px 0 6px 0;
  }
  .sg-lblb-title {
    font-family: 'Oswald','Bebas Neue',Impact,sans-serif;
    font-size: 1.4rem;
    letter-spacing: 0.22em;
    text-align: center;
    color: var(--sg-primary, #FFC43D);
    text-shadow: 0 0 14px rgba(255,196,61,0.4);
    padding: 10px 8px 8px 8px;
    border-bottom: 1px solid rgba(255,196,61,0.35);
    flex: 0 0 auto;
  }
  .sg-lblb-head {
    display: grid;
    grid-template-columns: 36px 1fr 70px 54px;
    gap: 8px;
    padding: 6px 10px 4px 10px;
    font-family: 'Oswald','Bebas Neue',Impact,sans-serif;
    font-size: 0.72rem;
    letter-spacing: 0.18em;
    color: #94A3B8;
    border-bottom: 1px dashed rgba(255,255,255,0.15);
    flex: 0 0 auto;
  }
  .sg-lblb-head > div:last-child,
  .sg-lblb-head > div:nth-last-child(2) {
    text-align: right;
  }
  .sg-lblb-body {
    flex: 1 1 auto;
    min-height: 0;
    overflow: hidden;
    position: relative;
  }
  .sg-lblb-list {
    display: flex;
    flex-direction: column;
    padding: 4px 0;
  }
  .sg-lblb-row {
    display: grid;
    grid-template-columns: 36px 1fr 70px 54px;
    gap: 8px;
    align-items: center;
    padding: 6px 10px;
    font-size: 0.95rem;
    border-top: 1px solid rgba(255,255,255,0.05);
  }
  .sg-lblb-row:first-child { border-top: none; }
  .sg-lblb-rank {
    font-family: 'Oswald','Bebas Neue',Impact,sans-serif;
    font-weight: 700;
    text-align: center;
    font-size: 1.05rem;
    color: #94A3B8;
  }
  .sg-lblb-name {
    overflow: hidden;
    white-space: nowrap;
    font-weight: 600;
    color: #E2E8F0;
  }
  .sg-lblb-points {
    text-align: right;
    font-weight: 700;
    color: var(--sg-primary, #FFC43D);
    font-variant-numeric: tabular-nums;
  }
  .sg-lblb-games {
    text-align: right;
    color: #94A3B8;
    font-variant-numeric: tabular-nums;
    font-size: 0.85rem;
  }
  .sg-lblb-row.sg-r1 { background: linear-gradient(90deg, rgba(255,196,61,0.18), rgba(255,196,61,0.0)); }
  .sg-lblb-row.sg-r2 { background: linear-gradient(90deg, rgba(192,192,192,0.15), rgba(192,192,192,0.0)); }
  .sg-lblb-row.sg-r3 { background: linear-gradient(90deg, rgba(205,127,50,0.15), rgba(205,127,50,0.0)); }
  .sg-lblb-row.sg-r1 .sg-lblb-rank { color: var(--sg-primary, #FFD166); text-shadow: 0 0 10px rgba(255,196,61,0.7); }
  .sg-lblb-row.sg-r2 .sg-lblb-rank { color: #D9D9D9; text-shadow: 0 0 8px rgba(217,217,217,0.5); }
  .sg-lblb-row.sg-r3 .sg-lblb-rank { color: #E0A060; text-shadow: 0 0 8px rgba(205,127,50,0.5); }
  .sg-lblb-empty {
    padding: 18px 12px;
    text-align: center;
    color: #94A3B8;
    font-style: italic;
    font-size: 0.9rem;
  }

  /* Ping-pong scroll for the tail panel. The --sg-lblb-travel CSS variable
     is set per-instance after layout so we only scroll by the exact
     distance by which the content overflows its container. */
  @keyframes sg-lblb-pingpong {
    0%   { transform: translateY(0); }
    100% { transform: translateY(var(--sg-lblb-travel, 0px)); }
  }
  .sg-lblb-scroll {
    animation: sg-lblb-pingpong var(--sg-lblb-duration, 40s) ease-in-out infinite alternate;
    will-change: transform;
  }
  .sg-lblb-scroll:hover { animation-play-state: paused; }
`;

/**
 * Injects the shared `.sg-lblb*` stylesheet once per document. Exported so the
 * in-game right-side leaderboard (ui/side-leaderboard.ts) can reuse the same
 * card treatment — rounded border, themed header, ranked rows, gold/silver/
 * bronze accents — without duplicating CSS.
 */
export function ensureLobbyLbStyles(): void {
  if (document.getElementById('sg-lblb-style')) return;
  const s = document.createElement('style');
  s.id = 'sg-lblb-style';
  s.textContent = STYLES;
  document.head.appendChild(s);
}

/** @deprecated Internal alias kept to avoid noisy edits — prefer ensureLobbyLbStyles. */
function ensureStyles(): void {
  ensureLobbyLbStyles();
}

/** Short-circuit for "no players" / "nobody in tail yet" states. */
function emptyMarkup(text: string): string {
  return `<div class="sg-lblb-empty">${text}</div>`;
}

function renderRow(r: LbRow, medalClass: string): HTMLDivElement {
  const row = document.createElement('div');
  row.className = `sg-lblb-row ${medalClass}`.trim();
  row.innerHTML = `
    <div class="sg-lblb-rank">${r.rank}</div>
    <div class="sg-lblb-name" data-role="name"></div>
    <div class="sg-lblb-points">${r.total_points.toLocaleString()}</div>
    <div class="sg-lblb-games">${r.games_played}</div>
  `;
  const nameEl = row.querySelector<HTMLDivElement>('[data-role="name"]')!;
  nameEl.textContent = r.display_name;
  // fit-text needs a laid-out container; schedule after attach.
  requestAnimationFrame(() => fitText(nameEl, { min: 10, max: 16 }));
  return row;
}

/**
 * In-place update of an existing row. Keeps the same DOM node so a repeated
 * setRows() call with mostly-unchanged data doesn't flash — we just edit the
 * text children. Re-runs fitText only when the display name actually changed.
 */
function updateRow(row: HTMLDivElement, r: LbRow, medalClass: string): void {
  const desiredCls = `sg-lblb-row ${medalClass}`.trim();
  if (row.className !== desiredCls) row.className = desiredCls;

  const rankEl = row.querySelector<HTMLDivElement>('.sg-lblb-rank');
  const nameEl = row.querySelector<HTMLDivElement>('[data-role="name"]');
  const pointsEl = row.querySelector<HTMLDivElement>('.sg-lblb-points');
  const gamesEl = row.querySelector<HTMLDivElement>('.sg-lblb-games');
  if (!rankEl || !nameEl || !pointsEl || !gamesEl) return;

  const rankStr = String(r.rank);
  if (rankEl.textContent !== rankStr) rankEl.textContent = rankStr;

  const pointsStr = r.total_points.toLocaleString();
  if (pointsEl.textContent !== pointsStr) pointsEl.textContent = pointsStr;

  const gamesStr = String(r.games_played);
  if (gamesEl.textContent !== gamesStr) gamesEl.textContent = gamesStr;

  if (nameEl.textContent !== r.display_name) {
    nameEl.textContent = r.display_name;
    requestAnimationFrame(() => fitText(nameEl, { min: 10, max: 16 }));
  }
}

function medalFor(rank: number): string {
  if (rank === 1) return 'sg-r1';
  if (rank === 2) return 'sg-r2';
  if (rank === 3) return 'sg-r3';
  return '';
}

/**
 * Build a stable identity for a row so repeated snapshots with the same
 * player ordering reuse the same DOM nodes. Falls back to rank when the
 * player_id is missing (shouldn't happen in prod, but be defensive).
 */
function rowKey(r: LbRow): string {
  return r.player_id != null ? `p:${r.player_id}` : `r:${r.rank}`;
}

/** Hash the relevant content of a row list so we can bail out early when
 *  successive snapshots contain identical leaderboard state. */
function rowsSignature(rows: LbRow[]): string {
  // Compact representation — join with `|` between rows, `,` between fields.
  let s = '';
  for (const r of rows) {
    s += `${r.player_id ?? ''},${r.rank},${r.total_points},${r.games_played},${r.display_name}|`;
  }
  return s;
}

/**
 * Top-10 panel (left column). Fixed-height, no scroll. Rank 1-3 rows get
 * gold/silver/bronze accents.
 */
export function createLobbyLeaderboardTop(mount: HTMLElement): LobbyLeaderboardHandle {
  ensureStyles();
  const el = document.createElement('div');
  el.className = 'sg-lblb';
  el.setAttribute('data-role', 'lobby-lb-top');
  el.innerHTML = `
    <div class="sg-lblb-title">TOP 10 ALL-TIME</div>
    <div class="sg-lblb-head"><div>#</div><div>PLAYER</div><div>POINTS</div><div>GAMES</div></div>
    <div class="sg-lblb-body"><div class="sg-lblb-list" data-role="list"></div></div>
  `;
  mount.appendChild(el);
  const list = el.querySelector<HTMLDivElement>('[data-role="list"]')!;

  // In-place bookkeeping: keep the last-rendered signature so we can short
  // circuit identical re-renders, and a key->node map so reordering touches
  // only what actually changed.
  let lastSig = '';
  let nodesByKey = new Map<string, HTMLDivElement>();
  let isEmpty = false;

  function setRows(rows: LbRow[]): void {
    const top = rows.slice(0, 10);
    const sig = rowsSignature(top);
    if (sig === lastSig) return;
    lastSig = sig;

    if (top.length === 0) {
      if (!isEmpty) {
        list.innerHTML = emptyMarkup('No scores yet — be the first!');
        nodesByKey = new Map();
        isEmpty = true;
      }
      return;
    }

    // Reconcile: reuse DOM nodes keyed by player_id where possible.
    const nextNodes = new Map<string, HTMLDivElement>();
    const fragment = document.createDocumentFragment();
    for (const r of top) {
      const key = rowKey(r);
      const medal = medalFor(r.rank);
      let node = nodesByKey.get(key);
      if (node) {
        updateRow(node, r, medal);
      } else {
        node = renderRow(r, medal);
      }
      nextNodes.set(key, node);
      fragment.appendChild(node);
    }
    // A single swap of children avoids layout thrash while preserving the
    // reused-node animations/selection state (there aren't any now, but this
    // makes future-me less sad).
    if (isEmpty) {
      // Leaving empty state — wipe the "no scores" markup.
      list.innerHTML = '';
      isEmpty = false;
    }
    // Replace children in one shot. Any dropped rows (player fell off top 10)
    // are removed automatically because they aren't in the fragment.
    list.replaceChildren(fragment);
    nodesByKey = nextNodes;
  }

  return {
    el,
    setRows,
    dispose() {
      el.remove();
    },
  };
}

/**
 * Ranks 11+ panel (right column). Pings between `translateY(0)` and
 * `translateY(-overflow)` via a pure-CSS animation. If the content doesn't
 * overflow the container the animation collapses to a no-op because
 * `--sg-lblb-travel` stays at 0px.
 *
 * setRows() is idempotent: when consecutive calls present the same rows (the
 * common case, because state_snapshot frames arrive on reconnect and lobby
 * ticks), we skip any DOM mutation. That preserves the in-flight CSS ping
 * pong keyframe instead of restarting it every snapshot.
 */
export function createLobbyLeaderboardTail(mount: HTMLElement): LobbyLeaderboardHandle {
  ensureStyles();
  const el = document.createElement('div');
  el.className = 'sg-lblb';
  el.setAttribute('data-role', 'lobby-lb-tail');
  el.innerHTML = `
    <div class="sg-lblb-title">STANDINGS</div>
    <div class="sg-lblb-head"><div>#</div><div>PLAYER</div><div>POINTS</div><div>GAMES</div></div>
    <div class="sg-lblb-body"><div class="sg-lblb-scroll" data-role="scroll"><div class="sg-lblb-list" data-role="list"></div></div></div>
  `;
  mount.appendChild(el);
  const body = el.querySelector<HTMLDivElement>('.sg-lblb-body')!;
  const scroll = el.querySelector<HTMLDivElement>('[data-role="scroll"]')!;
  const list = el.querySelector<HTMLDivElement>('[data-role="list"]')!;
  let resizeObs: ResizeObserver | null = null;

  let lastSig = '';
  let nodesByKey = new Map<string, HTMLDivElement>();
  let isEmpty = false;

  function recomputeTravel(): void {
    // Negative because we translate the scroll wrapper UP to reveal lower
    // rows. Clamp to 0 so non-overflowing lists don't jitter.
    const contentH = scroll.scrollHeight;
    const containerH = body.clientHeight;
    const overflow = Math.max(0, contentH - containerH);
    scroll.style.setProperty('--sg-lblb-travel', `${-overflow}px`);
    // Scale the duration with overflow so more content doesn't speed up.
    // Target roughly 80 px/sec (slow, readable). Minimum 35s per traversal.
    const dur = Math.max(35, Math.round(overflow / 80));
    scroll.style.setProperty('--sg-lblb-duration', `${dur}s`);
  }

  function setRows(rows: LbRow[]): void {
    const tail = rows.slice(10);
    const sig = rowsSignature(tail);
    if (sig === lastSig) return;
    lastSig = sig;

    if (tail.length === 0) {
      if (!isEmpty) {
        list.innerHTML = emptyMarkup('Only top 10 so far — more to come!');
        nodesByKey = new Map();
        isEmpty = true;
        requestAnimationFrame(recomputeTravel);
      }
      return;
    }

    const nextNodes = new Map<string, HTMLDivElement>();
    const fragment = document.createDocumentFragment();
    for (const r of tail) {
      const key = rowKey(r);
      let node = nodesByKey.get(key);
      if (node) {
        updateRow(node, r, '');
      } else {
        node = renderRow(r, '');
      }
      nextNodes.set(key, node);
      fragment.appendChild(node);
    }
    if (isEmpty) {
      list.innerHTML = '';
      isEmpty = false;
    }
    list.replaceChildren(fragment);
    nodesByKey = nextNodes;
    // Defer so DOM has laid out before we measure.
    requestAnimationFrame(recomputeTravel);
  }

  if (typeof ResizeObserver !== 'undefined') {
    resizeObs = new ResizeObserver(() => recomputeTravel());
    resizeObs.observe(body);
  }

  return {
    el,
    setRows,
    dispose() {
      if (resizeObs) resizeObs.disconnect();
      el.remove();
    },
  };
}
